import KinoklubbSelection from "@/public/KinoklubbSelection.svg";
import { Filmomtale, ICustomImage } from "@filmweb/sanity-types";
import FilmProgram from "components/film/FilmProgram";
import FullFacts from "components/film/FullFacts";
import GalleryContainer from "components/film/GalleryContainer";
import Oneliner from "components/film/Oneliner";
import OverriddenRichText from "components/film/OverriddenRichText";
import ShortFacts from "components/film/ShortFacts";
import TrailerContainer from "components/film/TrailerContainer";
import SArticleTitle from "components/framework/SArticleTitle";
import SMaxWidthContainer from "components/framework/SMaxWidthContainer";
import CMSImage, { CMSPicture, CMSPictureSource, OgImage, getFirstWideImageIndex } from "components/images/Image";
import { QUERY_FRAGMENT_BODYTEXT, QUERY_FRAGMENT_INGRESS } from "components/richtext/RichText";
import { toRem } from "lib/client/fonts";
import { useDocumentTitle } from "lib/client/useTitle";
import isKinoklubb from "lib/isKinoklubb";
import { setDefaultCacheHeaders } from "lib/server/cache";
import { fwpakkeQuery, getFilmwebFilmomtale, getServerData } from "lib/server/sanity";
import PublicRuntimeConfig from "lib/types/PublicRuntimeConfig";
import { DocumentType_MOVIE, FwPakkeImageDocument, ICommonProps, IMovieArticle, IQueryResult } from "lib/types/sanity";
import isPremiereInFuture from "lib/utils/isPremiereInFuture";
import { GetServerSideProps, GetServerSidePropsContext } from "next";
import getConfig from "next/config";
import dynamic from "next/dynamic";
import Head from "next/head";
import styled from "styled-components";

const { publicRuntimeConfig } = getConfig() as { publicRuntimeConfig: PublicRuntimeConfig };

const DynamicAdnuntiusAd = dynamic(() =>
	import('components/framework/AdnuntiusAd').then((mod) => mod.default),
	{ ssr: false }
);


//#region [Other]
const PAGE_QUERY: string = `
{
	"published": *[_type =="${DocumentType_MOVIE}" && edi.current == $edi && !(_id in path("drafts.**"))] {
		...,
		image->,
		${QUERY_FRAGMENT_BODYTEXT},
		${QUERY_FRAGMENT_INGRESS},
		horizontalPoster->,
	}[0],
	"preview": *[_type =="${DocumentType_MOVIE}" && edi.current == $edi && _id in path("drafts.**")] {
		...,
		image->,
		${QUERY_FRAGMENT_BODYTEXT},
		${QUERY_FRAGMENT_INGRESS},
		horizontalPoster->
	}[0]
}
`;
//#endregion

//#region [Other]getServerSideProps
export const getServerSideProps: GetServerSideProps = getServerData(async (context) => {
	const edi = context.params?.edi;
	if (!edi) {
		return { notFound: true };
	}
	setDefaultCacheHeaders(context as GetServerSidePropsContext);
	const filmwebMoviesPromise = getFilmwebFilmomtale([edi as string]);
	const pageDataPromise = fwpakkeQuery<IQueryResult<IMovieArticle>>(PAGE_QUERY, { edi: edi });

	const ctx = context as GetServerSidePropsContext;
	const isP = ctx.query?.p === "1" && context.preview;
	const queryResult = await pageDataPromise;
	const document = isP ? (queryResult?.preview ?? queryResult?.published) : queryResult?.published;

	// NOTE: Only the filmweb article is actually required to show the filmomtale page. The local article is optional

	const filmwebMovies = await filmwebMoviesPromise;
	/*if ((filmwebMovies?.length ?? 0) === 0) {
		return {
			notFound: true
		};
	}*/

	if (!document && (filmwebMovies?.length ?? 0) === 0) {
		return {
			notFound: true
		};
	}

	return {
		props: {
			document: document ?? null,
			filmomtale: filmwebMovies![0] ?? null
		}
	};


});
//#endregion



//#region [Props]
type FilmProps = {
	document: IMovieArticle | null;
	filmomtale: Filmomtale | null;
} & ICommonProps;
//#endregion

//#region [Component]
export default function Film({ filmomtale, document, appData }: FilmProps) {
	const poster = filmomtale?.mainPosterV2 ?? filmomtale?.postersV2?.[0];
	const headTitle = useDocumentTitle(appData.settings, (document ?? filmomtale)!);

	let title: string = (filmomtale?.title ?? document?.title)!;
	if (appData.settings.canOverrideMovieTitles && document?.titleOverride) {
		title = document.titleOverride;
	}

	const isPremFuture = isPremiereInFuture(filmomtale?.premiere, document?.premiereDate);

	return (<SMaxWidthContainer as="article">
		<Head>
			<title>{headTitle}</title>
			<meta property="og:title" content={headTitle} key="title" />
			<meta property="og:url" content={`${publicRuntimeConfig?.NEXT_PUBLIC_BASE_URL}film/${filmomtale?.mainVersionEDI ?? document?.edi.current}`} key="ogUrl" />

		</Head>
		<OgImage document={document} filmomtale={filmomtale} />
		<SArticleHeader>
			{filmomtale && isKinoklubb(filmomtale) && <SKinoKlubbSelection />}
			<STitle>{title}</STitle>
			<ShortFacts filmomtale={filmomtale} />
		</SArticleHeader>
		<SImageContainer>
			<FilmMainImage filmomtale={filmomtale} document={document} />
			{poster && <SPosterImage image={poster as (ICustomImage | FwPakkeImageDocument)} width={204} isFilmweb altText={`Plakat for '${filmomtale?.title}'`} />}
			{filmomtale && <TrailerContainer filmomtale={filmomtale} />}
		</SImageContainer>

		<FilmProgram edi={(filmomtale?.mainVersionEDI ?? document?.edi.current)!} nynorsk={appData.settings.nynorsk} userSettings={appData.userSettings} isKinoklubb={!!filmomtale && isKinoklubb(filmomtale)} isPremiereInFuture={isPremFuture} />
		<STextBlock>
			<Oneliner filmomtale={filmomtale} document={document} />
			<SIngress filmomtale={filmomtale} document={document} field="ingress" />
			<SBodyText filmomtale={filmomtale} document={document} field="bodyText" />
		</STextBlock>

		<DynamicAdnuntiusAd appData={appData} index={0} hideOnMobile={false} hideOnDesktop />

		<STextBlock>
			{filmomtale && <GalleryContainer overridenImage={filmomtale.mainImageGalleryV2 as ICustomImage} images={filmomtale.imagesV2 as ICustomImage[]} />}

			<FullFacts filmomtale={filmomtale} document={document} />
		</STextBlock>

	</SMaxWidthContainer>);
}

//#endregion


//#region [Styles]

const SImageContainer = styled.div`
	@media screen and (min-width: 641px) {
		position: relative;
	}

`;

const SArticleHeader = styled.header`

	margin-top: 1rem;

	@media screen and (max-width: 640px) {
		max-width: calc(100vh - 20px);
		margin: 100vw 10px 0 10px;
		color: white;
		background: rgba(0, 0, 0, 0.7);
		padding: 10px;

	}
`;

const SKinoKlubbSelection = styled(KinoklubbSelection)`
	display: block;
	max-width: 180px;
	height: auto;
	margin: 0 auto;
`;

const STitle = styled(SArticleTitle)`
	margin-bottom: 12px;
	margin-top: 0;
`;


const SMainImage = styled(CMSPicture)`

	height: auto;
	width: 100vw;
	left: calc(-50vw + 50%);
	position: fixed;
	z-index: -1;
	top: 0;


	@media screen and (min-width: 641px) {
		top: auto;
		z-index: 0;
		position: relative;
	}

`;

const SPosterImage = styled(CMSImage)`
	display: none;
	@media screen and (min-width: 641px) {
		display: block;
		height: auto;
		position: absolute;
		left: 0;
		border: 2px solid #ffffff;
		box-shadow: 10px 10px 30px 0px rgba(0, 0, 0, 0.5);

		${SMainImage}+& {
			bottom: -3.125rem;
		}

		&:only-child {
			bottom: auto;
			top: 0;
		}
	}
`;

const STextBlock = styled.div`
	gap: 50px;
	display: flex;
	flex-direction: column;
	margin: 50px 0 50px 0;
	@media screen and (max-width: 640px) {
		padding: 10px;
		background: rgba(0, 0, 0, 0.7);
	}

	p {
		&:first-child {
			margin-top: 0;
		}
		&:last-child {
			margin-bottom: 0;
		}
	}
`;

const SIngress = styled(OverriddenRichText)`
	font-size: 1rem;
	margin: 50px 0;
	font-weight: var(--font_bold);
	color: white;
	margin: 0;

	@media screen and (min-width: 641px) {
		padding: 0;
		color: currentColor;
		font-size: ${toRem(21)};
	}
`;

const SBodyText = styled(OverriddenRichText)`
	color: white;
	margin: 0;
	@media screen and (min-width: 641px) {
		color: currentColor;
	}
`;

//#endregion

//#region [Props]
type FilmMainImageProps = {
	document?: IMovieArticle | null;
	filmomtale?: Filmomtale | null;
}
//#endregion

//#region [Component]
function FilmMainImage({ document, filmomtale }: FilmMainImageProps) {
	let image = document?.image ?? filmomtale?.mainImageV2;
	if (!image && (filmomtale?.imagesV2?.length ?? 0) !== 0) {
		if (filmomtale) {
			const wimgIdx = getFirstWideImageIndex(filmomtale!.imagesV2 as ICustomImage[]);
			if (wimgIdx !== -1) {
				image = filmomtale.imagesV2![wimgIdx];
			}
		}
	}

	if (!image) {
		return null;
	}

	return <SMainImage image={image as (ICustomImage | FwPakkeImageDocument)} fallbackWidth={600} fallbackHeightRatio={1.48} isFilmweb={image._type === "customImage"} altText={`Bilde fra '${filmomtale?.title}'`}>
		<CMSPictureSource media="(min-width: 641px)" sizes={[641, 1440, 1920]} heightRatio={0.3} />
	</SMainImage>;
}

//#endregion